import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import InputField from '../../../Components/InputField';
import Button from '../../../Components/Button';
import palette from '../../../Utilities/Palette';
import LogoAnimation from '../../../Components/LogoAnimation';
import MutatingDots from '../../../Components/MutatingDots';

const Root = styled.div`
  opacity: 1;
  transition: opacity 0.25s;
  display: flex;
  justify-content: space-evenly;
  flex-direction: column;
  height: 100%;
`;

const LoginText = styled.div`
  font-size: 30px;
  font-weight: 250;
  text-align: left;

  @media (max-height: 576px) {
    font-size: 32px;
    font-weight: 500;
    text-align: left;
  }

  @media (max-width: 820px) {
    font-size: 32px;
    font-weight: 500;
    text-align: left;
  }
`;

const LogoText = styled.div`
  font-size: 40px;
  font-weight: 500;
  text-align: left;

  @media (max-height: 576px) {
    font-size: 40px;
    font-weight: 500;
    text-align: left;
  }

  @media (max-width: 820px) {
    font-size: 40px;
    font-weight: 500;
    text-align: left;
  }
`;

const CaptionText = styled.div`
  font-size: 18px;
  padding-top: 8px;
  font-weight: 300;
  text-align: left;

  @media (max-height: 576px) {
    font-size: 18px;
    padding-top: 8px;
    font-weight: 300;
    text-align: left;
  }

  @media (max-width: 820px) {
    font-size: 18px;
    padding-top: 8px;
    font-weight: 300;
    text-align: left;
  }
`;

const ForgottenPasswordText = styled.button`
  border-width: 0px;
  background-color: white;
  padding-top: 8px;
  padding-bottom: 8px;
  text-align: left;
  color: ${palette.PRIMARY};
  text-decoration: underline;
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  padding: 8px;
  display: flex;
  justify-content: center;
`;

type Props = {
  loading: boolean;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  onClickForgotPassword: () => void;
  onClickLogin: () => void;
  onClickRegister: () => void;
};

export default function LoginDetails({
  setEmail,
  setPassword,
  onClickForgotPassword,
  onClickLogin,
  onClickRegister,
  loading,
}: Props) {
  const { t } = useTranslation();
  return (
    <Root>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <LogoAnimation />
      </div>
      <InputField
        width="100%"
        mobileWidth="100%"
        tabletWidth="100%"
        mandatory
        placeholder="you@example.com"
        header={t('generic.email_address', 'Email Address')}
        onChange={setEmail}
        onEnter={onClickLogin}
      />
      <InputField
        width="100%"
        mobileWidth="100%"
        tabletWidth="100%"
        mandatory
        placeholder={t(
          'login.password_character_length',
          'Enter 6 characters or more',
        )}
        header={t('generic.password', 'Password')}
        type="password"
        onChange={setPassword}
        onEnter={onClickLogin}
      />
      <ForgottenPasswordText onClick={onClickForgotPassword}>
        {t('login.forgot_password', 'Forgot Password?')}
      </ForgottenPasswordText>
      <ButtonContainer>
        <Button
          icon={
            loading ? (
              <div
                style={{
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: -45,
                    left: -40,
                    zIndex: 9999,
                    zoom: 0.5,
                  }}
                >
                  <MutatingDots />
                </div>
              </div>
            ) : null
          }
          name={loading ? '' : t('login.title', 'Login')}
          onClick={onClickLogin}
        />
      </ButtonContainer>
      <ButtonContainer>
        <Button
          type="secondary"
          textColor="black"
          name="Register"
          onClick={onClickRegister}
        />
      </ButtonContainer>
    </Root>
  );
}
